/**
 * The networks a household has given to *besides* the viewer's own, shaped as
 * `POST /user/<userID>/giving` returns them in `data.additionalGivingNetworks`:
 * already filtered to what the viewer may open, and already in display order.
 * The viewer's own network is never in the list — the chip puts it in row 1
 * itself.
 *
 * profile2 sends the same list, so the closed chip's logo stack can show it
 * before the giving tab has loaded.
 *
 * This is the one set of networks behind every way in. Tests pass a scenario
 * through `fnRender`'s `initialGlobalState` under `userProfile` (the giving
 * tab's list), or render a user whose `mockData/user/<userID>/profile2.js`
 * returns one (profile2's list). They never go through the development mock
 * flag; that flag's `Flocknote/mockData/` folder reads its networks from here,
 * so deleting the folder leaves these tests untouched.
 *
 * @example
 * fnRender(<ProfileSidebar />, {
 *   url: `/user/${userID}`,
 *   initialGlobalState: {
 *     userProfile: { additionalGivingNetworks: givingNetworkScenarios.parent },
 *   },
 * });
 */

/** Data-URI logo, so a fixture renders identically offline and in CI. */
const logo = (initials, background) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
      <rect width="64" height="64" rx="10" fill="${background}"/>
      <text x="32" y="41" font-family="Raleway, sans-serif" font-size="26"
            font-weight="600" fill="#fff" text-anchor="middle">${initials}</text>
    </svg>`
  )}`;

const DIOCESE = {
  networkID: 1,
  userID: 5001,
  name: "Diocese of Denver",
  url: "https://denver.flocknote.com",
  isParentNet: true,
  imageURL: logo("DD", "#15436a"),
};

const ST_MARYS = {
  networkID: 101,
  userID: 5101,
  name: "St. Mary's",
  url: "https://stmarys.flocknote.com",
  isParentNet: false,
  imageURL: logo("SM", "#278dc1"),
};

const ST_JUDE = {
  networkID: 102,
  userID: 5102,
  name: "St. Jude",
  url: "https://stjude.flocknote.com",
  isParentNet: false,
  imageURL: logo("SJ", "#2cd261"),
};

const HOLY_FAMILY = {
  networkID: 103,
  userID: 5103,
  name: "Holy Family",
  url: "https://holyfamily.flocknote.com",
  isParentNet: false,
  imageURL: logo("HF", "#d5a223"),
};

const ST_THOMAS = {
  networkID: 104,
  userID: 5104,
  name: "St. Thomas More",
  url: "https://stthomasmore.flocknote.com",
  isParentNet: false,
  imageURL: logo("TM", "#8e5bd1"),
};

/**
 * A real Flocknote network, reported 2026-09-17: seven merged parishes sharing
 * one network, each named in full. 179 characters — the longest name this chip
 * is known to have to hold.
 */
const MERGED_PARISHES = {
  networkID: 105,
  userID: 5105,
  name:
    "Parishes of St. Peter - Franklin, Ss. Mark and John - Antioch, " +
    "St. Bartholomew - Eastside, St. Mary - Silver Springs, " +
    "St. Luke - Duluth, St. Thomas - South York and St. George - London",
  url: "https://mergedparishes.flocknote.com",
  isParentNet: false,
  imageURL: logo("PP", "#c14b27"),
};

const givingNetworkScenarios = {
  /** On the diocese, gave to four parishes — four rows, so "More" is exercised. */
  parent: [ST_MARYS, ST_JUDE, HOLY_FAMILY, ST_THOMAS],
  /** On a parish, gave to the diocese — one row, so "More" is not. */
  child: [DIOCESE],
  /** Gave to two other networks — three rows, and a "More" would hide only one. */
  twoOthers: [ST_MARYS, ST_JUDE],
  /** Gave only to the viewer's own network — today's plain Giving button. */
  none: [],
  /** One 179-character name among ordinary ones, which is how the real one reads. */
  superLong: [MERGED_PARISHES, ST_JUDE, HOLY_FAMILY, ST_THOMAS],
};

export default givingNetworkScenarios;
